import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import { TypesenseSearchService } from '@/lib/search/typesense/SearchService';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get search query from URL - support both 'q' and 'keyword' params
  const searchQuery = searchParams.get('q') || searchParams.get('keyword') || '';

  // Fetch both display and sequence results for smart routing
  const { data: displayResults } = useQuery({
    queryKey: ['smart-search-displays', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return null;
      return await TypesenseSearchService.searchDisplays({
        query: searchQuery,
        per_page: 1 // We only need the count
      });
    },
    enabled: !!searchQuery.trim(),
  });

  const { data: sequenceResults } = useQuery({
    queryKey: ['smart-search-sequences', searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim()) return null;
      return await TypesenseSearchService.searchSequences({
        query: searchQuery,
        per_page: 1 // We only need the count
      });
    },
    enabled: !!searchQuery.trim(),
  });

  // Perform smart routing once we have both results
  useEffect(() => {
    if (!searchQuery.trim()) {
      // If no query, redirect to sequences page
      navigate('/sequences', { replace: true });
      return;
    }

    // Wait for both queries to complete
    if (displayResults !== undefined && sequenceResults !== undefined) {
      const displayCount = displayResults?.found || 0;
      const sequenceCount = sequenceResults?.found || 0;

      // Route to the type with more results, tie-breaker goes to sequences
      if (displayCount > sequenceCount && displayCount > 0) {
        navigate(`/displays?q=${encodeURIComponent(searchQuery)}`, { replace: true });
      } else {
        navigate(`/sequences?q=${encodeURIComponent(searchQuery)}`, { replace: true });
      }
    }
  }, [searchQuery, displayResults, sequenceResults, navigate]);

  // Show loading state while determining where to redirect
  return (
    <>
      <Helmet>
        <title>{searchQuery ? `Searching for "${searchQuery}"` : 'Search'} | LightsHunt</title>
        <meta name="description" content="Finding the best results for your search..." />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />

        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
            <h2 className="text-xl font-semibold mb-2">
              {searchQuery ? `Searching for "${searchQuery}"...` : 'Redirecting...'}
            </h2>
            <p className="text-muted-foreground">
              Finding the best results for you
            </p>
          </div>
        </main>
      </div>
    </>
  );
} 